import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Magnet,
  Clapperboard,
  Smartphone,
  BarChart3,
  TrendingUp,
  Sparkles,
  FileText,
  BookOpen,
  Instagram,
  Youtube,
  Music2,
  GraduationCap,
} from "lucide-react";

/* ─── Data ─── */

const IDEAS = [
  {
    title: "3 błędy, przez które nie rośniesz na IG",
    tag: "Edukacyjne",
    metric: "128K",
    emoji: "🎓",
    thumbBg: "from-blue-400 to-indigo-500",
  },
  {
    title: "Nikt Ci tego nie mówi o rolkach...",
    tag: "Motywacyjne",
    metric: "95K",
    emoji: "🔥",
    thumbBg: "from-orange-400 to-rose-500",
  },
  {
    title: "Jak zdobyć 1000 obserwujących od zera",
    tag: "Poradnik",
    metric: "103K",
    emoji: "🚀",
    thumbBg: "from-emerald-400 to-teal-500",
  },
];

const QUICK_ACTIONS = [
  { Icon: Clapperboard, label: "Kreator scenariusza", gradient: "from-purple-400 to-violet-600" },
  { Icon: FileText, label: "Biblioteka", gradient: "from-blue-400 to-indigo-600" },
  { Icon: BookOpen, label: "Mózg Viraluj", gradient: "from-pink-400 to-rose-600" },
  { Icon: BarChart3, label: "Analityka", gradient: "from-emerald-400 to-teal-600" },
];

// Positioned so each card hugs the phone's edge and the pair on each side
// staggers vertically (top card higher, bottom card lower — and the right
// column offset from the left one) instead of sitting in a flat row.
const FLOATING_FEATURES = [
  {
    Icon: Magnet,
    title: "Znajdź pomysł",
    subtitle: "Gotowe inspiracje i trendy",
    gradient: "from-rose-400 to-red-500",
    posClass: "right-full top-[0%] sm:top-[3%] mr-2.5 sm:mr-3.5 md:mr-4 lg:mr-5",
    arrowClass: "right-full top-[9%] sm:top-[12%] mr-0",
    arrowFlip: false,
  },
  {
    Icon: Clapperboard,
    title: "Stwórz scenariusz",
    subtitle: "Gotowe w 60 sekund",
    gradient: "from-purple-400 to-violet-600",
    posClass: "right-full bottom-[4%] sm:bottom-[6%] mr-2.5 sm:mr-3.5 md:mr-4 lg:mr-5",
    arrowClass: "right-full bottom-[11%] sm:bottom-[13%] mr-0",
    arrowFlip: false,
  },
  {
    Icon: Smartphone,
    title: "Nagraj rolkę",
    subtitle: "Z łatwością i pewnością",
    gradient: "from-blue-400 to-indigo-600",
    posClass: "left-full top-[12%] sm:top-[15%] ml-2.5 sm:ml-3.5 md:ml-4 lg:ml-5",
    arrowClass: "left-full top-[21%] sm:top-[24%] ml-0",
    arrowFlip: true,
  },
  {
    Icon: BarChart3,
    title: "Sprawdź wyniki",
    subtitle: "Analizuj i rośnij szybciej",
    gradient: "from-emerald-400 to-teal-600",
    posClass: "left-full bottom-[16%] sm:bottom-[18%] ml-2.5 sm:ml-3.5 md:ml-4 lg:ml-5",
    arrowClass: "left-full bottom-[23%] sm:bottom-[25%] ml-0",
    arrowFlip: true,
  },
] as const;

const PILLARS = [
  {
    Icon: Magnet,
    gradient: "from-rose-400 to-red-500",
    title: "Pomysły, które działają",
    desc: "Sprawdzone formaty, trendy i inspiracje dopasowane do Ciebie.",
  },
  {
    Icon: Clapperboard,
    gradient: "from-purple-400 to-violet-600",
    title: "Scenariusze, które wciągają",
    desc: "Gotowe hooki, struktury i teksty, które zatrzymują uwagę widza.",
  },
  {
    Icon: BarChart3,
    gradient: "from-emerald-400 to-teal-600",
    title: "Wyniki, które napędzają",
    desc: "Dowiedz się, co działa na Twoim profilu i twórz treści, które przynoszą efekty.",
  },
  {
    Icon: GraduationCap,
    gradient: "from-orange-400 to-orange-600",
    title: "Szkolenie, które uczy",
    desc: "Akademia Instagrama — 11 modułów i 30+ lekcji krok po kroku.",
  },
];

// Instagram is live today; TikTok and YouTube are announced but not yet
// shipped, so they're shown muted with a "wkrótce" tag instead of implying
// they already work. Reels was dropped — it's an Instagram format, not a
// separate platform, so it duplicated the Instagram entry.
const PLATFORMS = [
  { Icon: Instagram, label: "Instagram", active: true },
  { Icon: Music2, label: "TikTok", active: false },
  { Icon: Youtube, label: "YouTube", active: false },
];

/* ─── Small decorative bits ─── */

const ConnectorDoodle = ({ flip = false }: { flip?: boolean }) => (
  <svg
    viewBox="0 0 40 24"
    fill="none"
    aria-hidden="true"
    className={`w-4 h-2.5 sm:w-6 sm:h-3.5 md:w-8 md:h-5 lg:w-10 lg:h-6 ${flip ? "scale-x-[-1]" : ""}`}
  >
    <path d="M2 12 C 12 4, 24 4, 34 12" stroke="#60A5FA" strokeWidth="3" strokeLinecap="round" strokeDasharray="1 7" />
    <path d="M28 6 L36 12 L28 18" stroke="#60A5FA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

// Bigger decorative squiggle pointing from the CTA copy down toward the phone.
const CtaDoodle = () => (
  <svg
    width="90"
    height="70"
    viewBox="0 0 90 70"
    fill="none"
    aria-hidden="true"
    className="hidden sm:block absolute -right-16 md:-right-20 top-1/2 -translate-y-1/2 text-primary/50"
  >
    <path
      d="M6 4 C 40 4, 10 40, 62 46"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeDasharray="1 8"
    />
    <path d="M52 38 L64 47 L54 58" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const FloatingCard = ({
  Icon,
  title,
  subtitle,
  gradient,
}: {
  Icon: typeof Magnet;
  title: string;
  subtitle: string;
  gradient: string;
}) => (
  <div className="w-[min(20vw,92px)] sm:w-[116px] md:w-[148px] lg:w-[176px] rounded-xl sm:rounded-2xl bg-white border border-gray-100 shadow-lg shadow-black/[0.08] p-2 sm:p-3 md:p-3.5 lg:p-4 flex flex-col items-center text-center gap-1 sm:gap-1.5">
    <div
      className={`w-7 h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-11 lg:h-11 rounded-lg sm:rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-md`}
    >
      <Icon className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5 md:w-5 md:h-5 text-white" strokeWidth={2} />
    </div>
    <p className="text-[9px] sm:text-[12px] md:text-[14px] lg:text-[15px] font-bold text-gray-900 leading-tight">
      {title}
    </p>
    <p className="text-[7.5px] sm:text-[10px] md:text-[11.5px] lg:text-[12.5px] text-gray-500 leading-tight">
      {subtitle}
    </p>
  </div>
);

const PhoneShowcase = () => (
  <div className="relative z-10 w-[min(48vw,215px)] sm:w-[260px] md:w-[300px] lg:w-[340px] mx-auto flex-shrink-0">
    <div className="aspect-[1/2] rounded-[1.8rem] sm:rounded-[2.1rem] md:rounded-[2.4rem] bg-gray-900 p-1.5 sm:p-2 shadow-2xl flex flex-col">
      <div className="flex-1 flex flex-col rounded-[1.4rem] sm:rounded-[1.7rem] md:rounded-[2rem] bg-white overflow-hidden relative">
        {/* Notch */}
        <div className="absolute top-1.5 sm:top-2 left-1/2 -translate-x-1/2 w-12 sm:w-16 md:w-20 h-3.5 sm:h-4.5 md:h-5 rounded-full bg-gray-900 z-10" />

        {/* Status bar */}
        <div className="flex items-center justify-between px-3.5 sm:px-4.5 md:px-5 pt-2 sm:pt-3 pb-1 text-[8px] sm:text-[10px] md:text-[12px] font-semibold text-gray-900">
          <span>9:41</span>
          <div className="flex items-center gap-1 text-gray-800">
            <div className="flex items-end gap-[1.5px]">
              <span className="w-[2.5px] h-[3.5px] sm:h-[4.5px] bg-current rounded-[1px]" />
              <span className="w-[2.5px] h-[5px] sm:h-[6.5px] bg-current rounded-[1px]" />
              <span className="w-[2.5px] h-[7px] sm:h-[9px] bg-current rounded-[1px]" />
            </div>
            <div className="w-[12px] sm:w-[15px] h-[8px] sm:h-[10px] rounded-[2px] border border-current relative ml-1">
              <span className="absolute inset-[1.5px] bg-current rounded-[0.5px]" />
            </div>
          </div>
        </div>

        {/* Greeting */}
        <div className="flex items-center justify-between px-3 sm:px-4 pt-2 sm:pt-3 pb-2 sm:pb-2.5">
          <div>
            <p className="text-[10px] sm:text-[14px] md:text-[16px] font-bold text-gray-900 leading-tight">
              Cześć! 👋
            </p>
            <p className="text-[8px] sm:text-[11px] md:text-[13px] text-gray-500">Co dziś nagrasz?</p>
          </div>
          <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full bg-orange-100 flex items-center justify-center text-xs sm:text-base md:text-xl flex-shrink-0">
            🦊
          </div>
        </div>

        {/* Magnes Pomysłów card */}
        <div className="mx-3 sm:mx-4 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-100 bg-white shadow-sm p-2 sm:p-2.5 md:p-3.5">
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
            <div className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-md sm:rounded-lg bg-gradient-to-br from-rose-400 to-red-500 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Magnet className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-white" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-[8px] sm:text-[11px] md:text-[13.5px] font-bold text-gray-900 leading-none">
                Magnes Pomysłów
              </p>
              <p className="hidden sm:block text-[9px] md:text-[11px] text-gray-500 mt-1 leading-tight">
                Codziennie nowe pomysły dopasowane do Ciebie.
              </p>
            </div>
          </div>

          <div className="space-y-1 sm:space-y-1.5">
            {IDEAS.map((idea) => (
              <div key={idea.title} className="flex items-start gap-1.5 sm:gap-2 rounded-md sm:rounded-lg bg-gray-50 p-1 sm:p-1.5">
                <div
                  className={`w-5 h-5 sm:w-7 sm:h-7 md:w-9 md:h-9 rounded sm:rounded-md bg-gradient-to-br ${idea.thumbBg} flex items-center justify-center text-[9px] sm:text-xs md:text-sm flex-shrink-0`}
                >
                  {idea.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[7.5px] sm:text-[9px] md:text-[11px] font-semibold text-gray-800 leading-tight line-clamp-2">
                    {idea.title}
                  </p>
                  <span className="text-[6.5px] sm:text-[8px] md:text-[9.5px] text-primary font-medium">
                    {idea.tag}
                  </span>
                </div>
                <span className="text-[7px] sm:text-[9px] md:text-[11px] font-bold text-emerald-600 flex items-center gap-0.5 flex-shrink-0 mt-0.5">
                  <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3" strokeWidth={2.5} />
                  {idea.metric}
                </span>
              </div>
            ))}
          </div>

          <p className="mt-1.5 sm:mt-2 text-[7.5px] sm:text-[9px] md:text-[11px] font-semibold text-primary text-center">
            Zobacz więcej pomysłów →
          </p>
        </div>

        {/* Sticky note — trend callout below the Magnes Pomysłów card */}
        <div className="mx-3 sm:mx-4 mt-2 sm:mt-2.5">
          <div className="rotate-[-2deg] rounded-md sm:rounded-lg bg-yellow-200 shadow-sm px-2 py-1.5 sm:px-2.5 sm:py-2 flex items-center justify-between gap-1.5">
            <span className="text-[7px] sm:text-[9px] md:text-[10.5px] font-bold text-gray-800 leading-tight">
              🔥 Nowy trend na IG
            </span>
            <span className="text-[6.5px] sm:text-[8px] md:text-[9.5px] font-semibold text-gray-600 underline underline-offset-2 flex-shrink-0">
              Zobacz →
            </span>
          </div>
        </div>

        {/* Quick actions */}
        <div className="px-3 sm:px-4 pt-2 sm:pt-3 pb-2.5 sm:pb-4">
          <p className="text-[6.5px] sm:text-[8px] md:text-[9.5px] font-semibold text-gray-400 uppercase tracking-wider mb-1 sm:mb-1.5">
            Szybkie akcje
          </p>
          <div className="grid grid-cols-4 gap-1 sm:gap-1.5">
            {QUICK_ACTIONS.map(({ Icon, label, gradient }) => (
              <div key={label} className="rounded-md sm:rounded-xl bg-gray-50 p-1 sm:p-1.5 md:p-2 flex flex-col items-center gap-0.5 sm:gap-1 text-center">
                <div className={`w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded sm:rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center shadow-sm`}>
                  <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-white" strokeWidth={2} />
                </div>
                <span className="hidden sm:block text-[7px] md:text-[8px] font-semibold text-gray-700 leading-tight">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Spacer + home indicator — absorbs any leftover height from the
            forced 1:2 aspect ratio instead of leaving a dead empty gap. */}
        <div className="flex-1 flex items-end justify-center pb-1.5 sm:pb-2">
          <div className="w-16 sm:w-20 md:w-24 h-1 rounded-full bg-gray-200" />
        </div>
      </div>
    </div>
  </div>
);

/* ─── Section ─── */

const AppShowcaseHero = () => {
  const navigate = useNavigate();

  return (
    <>
      {/* A — Showcase: headline, CTA, app showcase.
          Sits right after PricingSection's blue ending now, not at the
          top of the page, so it gets a top bridge instead of the big
          fixed-header clearance padding it used to need. */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-[#F7F4EF] pt-16 sm:pt-20 md:pt-24 pb-16 md:pb-20 px-2 sm:px-4">
        <div
          className="absolute inset-x-0 top-0 h-32 md:h-40 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, #006AD5, transparent)" }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
          style={{
            background: "radial-gradient(ellipse 70% 60% at 50% 0%, rgba(0,128,255,0.08) 0%, transparent 70%)",
          }}
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center text-center">
          {/* Headline */}
          <h1 className="text-[32px] leading-[1.1] sm:text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 sm:leading-[1.05]">
            Nie musisz wiedzieć,
            <br />
            co nagrać.
            <br />
            <span className="text-primary">Viraluj Ci to pokaże.</span>
          </h1>

          <p className="mt-5 max-w-xl text-base md:text-lg text-gray-600 leading-relaxed">
            Znajdź pomysł, stwórz scenariusz i nagrywaj rolki, które przyciągają uwagę i budują Twoją społeczność.
          </p>

          {/* CTA */}
          <div className="relative mt-7 flex flex-col items-center gap-2">
            <CtaDoodle />
            <Button
              size="lg"
              onClick={() => navigate("/auth?mode=register&trial=true")}
              className="px-8 py-6 text-base font-bold bg-primary text-white hover:bg-primary/90 rounded-full shadow-lg shadow-primary/25"
            >
              <Sparkles className="mr-1 w-4 h-4" />
              Zacznij za darmo
            </Button>
            <p className="text-xs text-gray-500">5 dni za darmo • Zacznij w kilka sekund</p>
          </div>

          {/* App showcase — cards hug the phone's corners at every breakpoint */}
          <div className="relative mt-16 sm:mt-14 md:mt-16 w-full flex items-center justify-center">
            {/* Glow behind the phone */}
            <div
              className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full blur-lg sm:blur-xl"
              style={{
                width: "min(78vw, 520px)",
                height: "min(78vw, 520px)",
                background:
                  "radial-gradient(circle, rgba(0,128,255,0.55) 0%, rgba(0,128,255,0.28) 45%, transparent 72%)",
              }}
              aria-hidden="true"
            />

            <div className="relative">
              <PhoneShowcase />

              {FLOATING_FEATURES.map((f) => (
                <div key={f.title} className={`absolute z-10 ${f.posClass}`}>
                  <FloatingCard {...f} />
                </div>
              ))}
              {FLOATING_FEATURES.map((f) => (
                <div key={`${f.title}-arrow`} className={`absolute z-10 ${f.arrowClass}`}>
                  <ConnectorDoodle flip={f.arrowFlip} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* B — Wszystko, czego potrzebujesz w jednym miejscu.
          Same #F7F4EF as HowItWorksSection right below, so no bridge needed. */}
      <section className="relative bg-[#F7F4EF] pt-4 pb-16 md:pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
            Wszystko, czego potrzebujesz
            <br />
            <span className="text-primary">w jednym miejscu</span>
          </h2>

          <div className="mt-8 sm:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
            {PILLARS.map((p) => (
              <div key={p.title} className="flex flex-col items-center gap-1.5 sm:gap-3">
                <div
                  className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br ${p.gradient} flex items-center justify-center shadow-md`}
                >
                  <p.Icon className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-xs sm:text-base md:text-lg font-bold text-gray-900 leading-tight">{p.title}</h3>
                <p className="text-[10px] sm:text-sm text-gray-500 leading-snug sm:leading-relaxed max-w-[240px]">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 sm:mt-10 flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-8 gap-y-3">
            {PLATFORMS.map(({ Icon, label, active }) => (
              <div
                key={label}
                className={`flex items-center gap-1.5 text-sm font-semibold ${active ? "text-gray-700" : "text-gray-300"}`}
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                {label}
                {!active && (
                  <span className="text-[9px] font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                    wkrótce
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AppShowcaseHero;
